import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SEO from "../components/SEO";
import { Reveal, SectionLabel } from "../components/Motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const FAQS = [
  {
    q: "Do you manufacture the products yourselves?",
    a: "HAQILA operates as an Indonesian commodity sourcing and export partner. We work directly with selected manufacturers and producers rather than claiming to manufacture every product ourselves.",
  },
  {
    q: "Can I request a product sample?",
    a: "Yes. Sample arrangements can be discussed depending on the product and manufacturing partner.",
  },
  {
    q: "What is your MOQ?",
    a: "MOQ generally starts from one container for bulk export orders. Actual MOQ depends on product, packaging and supplier requirements.",
  },
  {
    q: "Do you provide COA?",
    a: "Product COA and specification documentation can be provided depending on the product and manufacturing partner.",
  },
  {
    q: "Do you offer private label?",
    a: "Private-label and OEM packaging may be available depending on the product, order quantity and manufacturing partner.",
  },
  {
    q: "Can you provide CIF pricing?",
    a: "Yes. CIF quotations can be arranged based on the destination port and current freight rates.",
  },
  {
    q: "What is your typical lead time?",
    a: "Typical lead time is approximately 3\u20134 weeks after order confirmation and required payment, subject to product specification, packaging requirements, quantity and production schedule.",
  },
  {
    q: "Can I visit the manufacturing facility?",
    a: "Factory visits may be arranged for selected products and manufacturing partners, subject to availability and prior coordination.",
  },
];

export default function Faq() {
  return (
    <>
      <SEO />
      <div data-testid="faq-page" className="bg-offwhite pt-40 pb-24 sm:pb-32 min-h-screen">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
          <SectionLabel index="FAQ" title="Buyer Questions" />
          <Reveal className="mt-8 max-w-4xl">
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.98]">
              Frequently Asked Questions
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-charcoal/65 leading-relaxed">
              Straightforward answers for international buyers evaluating Indonesian sourcing.
            </p>
          </Reveal>

          <Reveal delay={0.2} className="mt-14 max-w-4xl">
            <Accordion type="single" collapsible data-testid="faq-accordion" className="border-t border-charcoal/10">
              {FAQS.map((faq, i) => (
                <AccordionItem key={faq.q} value={`faq-${i}`} className="border-b border-charcoal/10">
                  <AccordionTrigger
                    data-testid={`faq-question-${i + 1}`}
                    className="py-6 text-left font-display text-lg sm:text-xl font-bold tracking-tight hover:text-forest hover:no-underline transition-colors duration-300"
                  >
                    <span className="flex items-baseline gap-5">
                      <span className="text-xs font-semibold tracking-[0.3em] text-charcoal/35 shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      {faq.q}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent
                    data-testid={`faq-answer-${i + 1}`}
                    className="pl-10 sm:pl-12 pb-6 text-sm sm:text-base text-charcoal/65 leading-relaxed max-w-2xl"
                  >
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>

          <Reveal className="mt-20 bg-charcoal text-offwhite p-10 sm:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Let's talk about your supply.</h2>
              <p className="mt-3 text-sm text-offwhite/60 max-w-lg leading-relaxed">
                Tell us what you need. We'll review your requirements and identify suitable sourcing options from
                Indonesia.
              </p>
            </div>
            <Link
              to="/request-a-quote"
              data-testid="faq-quote-cta"
              className="group inline-flex items-center gap-3 bg-forest hover:bg-moss text-offwhite font-semibold px-8 py-4 text-sm tracking-wide transition-colors duration-300 shrink-0"
            >
              Request a Quote
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </div>
    </>
  );
}
